<template>
  <div class='content'>
    <Grid v-if="!showCreateEnvelope"
          ref="grid"
          :name='$t("EvedPay Cards")'
          desc='Manage accounts, cards and transactions'
          search=true
          search-placeholder='My Transactions'
          resizing=false
          :endpoint="api.$.evedpaycard.transactions"
          :sort="sort"
          legacy
          footer
          :row-classes="rowClasses"
          :tabs='tabs'
          alias="EvedPayCards"
          @selectedRows="rowSelected"
          :no-tooltip-fields="noTooltipFields"
          :filter="{}"
          totals>
      <span slot="additionalButtons">
        <!-- PRODUCTDEV-5788 — global table: show only if the feature flag is on AND the user has
             at least one scan-enabled division (hidden when every division is "No Receipt Capture"). -->
        <ed-button class="btn btn-actionbar"
                   v-if="receiptScanEnabled && anyScanEnabled"
                   :disabled="uploadingReceipt"
                   @click="openReceiptUpload">
          SUBMIT RECEIPT
        </ed-button>
        <input type="file"
               ref="receiptUploadInput"
               accept="image/*,application/pdf"
               @change="handleReceiptFileSelected"
               style="display:none">
        <ed-button class="btn btn-actionbar"
                   :disabled="cardActionDisabled('createEnvelope')"
                   v-tooltip="actionDisabledReason('createEnvelope')"
                   @click="createEnvelope">
          CREATE ENVELOPE
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   :disabled="cardActionDisabled('reconcile')"
                   v-tooltip="actionDisabledReason('reconcile')"
                   @click="showModal(showReconcile)">
          RECONCILE
        </ed-button>
      </span>
      <span slot="additionalLabel" class="form-group no-margin">
        <i class="fas fa-exclamation"></i>
        <span>&nbsp;</span>
        <label>Click column headers to apply filters</label>
      </span>
    </Grid>
    <CreateEnvelope title="CREATE ENVELOPE"
                    :initial-rows="selectedRows"
                    :envelope="envelope"
                    v-if="showCreateEnvelope"
                    @success="showCreateEnvelope=!showCreateEnvelope; selectedRows = []; $router.push('/d/Programs/>Card/envelopes')"
                    @backToTransactions="showCreateEnvelope=!showCreateEnvelope; updateUrlBackToTransactionsPage(); selectedRows = []; envelope = {}"
                    @fields-updated="handleFieldsUpdated">
    </CreateEnvelope>
    <CandidateSelector
      :show="showCandidateSelector"
      :receipt-scan-id="candidateSelectorReceiptId"
      :candidates="candidateSelectorCandidates"
      :row-version="candidateSelectorRowVersion"
      :auto-linked="candidateSelectorAutoLinked"
      :auto-fill="candidateSelectorAutoFill"
      :is-mobile="false"
      :claim-mode="false"
      :loading="candidateSelectorLoading"
      :local-image-url="candidateSelectorLocalImageUrl"
      :local-image-content-type="candidateSelectorLocalImageType"
      @match-attempt="handleMatchAttempt"
      @match-selected="handleMatchSelected"
      @match-error="handleMatchError"
      @save-to-folder="handleSaveToFolder"
      @close="handleCandidateSelectorClose"
    />
    <ed-modal title="Submit Receipt"
              :show="showSubmitReceiptModal"
              @close="closeSubmitReceiptModal"
              @validate="confirmSubmitReceipt"
              primary-button="Submit"
              :primaryButtonDisabled="!pendingReceiptFile || uploadingReceipt"
              type="medium"
              validation>
      <div slot="body">
        <p style="margin-bottom: 18px; white-space: nowrap;">Upload an image of a receipt and it will be matched to a transaction and coded automatically.</p>
        <template v-if="uploadingReceipt">
          <label><strong>Uploading</strong></label>
          <div style="display: flex; align-items: center; gap: 8px; padding: 12px 0 24px;">
            <div style="flex: 1; height: 8px; background: #F5F5F5; border-radius: 100px; overflow: hidden;">
              <div :style="{ width: uploadProgressPct + '%', height: '100%', background: '#1890FF', borderRadius: '100px' }"></div>
            </div>
            <span style="font-family: Roboto, sans-serif; font-size: 14px; line-height: 22px; color: rgba(0, 0, 0, 0.85); width: 32px; text-align: right;">{{ uploadProgressPct }}%</span>
          </div>
        </template>
        <template v-else>
          <label><strong>Receipt</strong></label>
          <div v-if="!pendingReceiptFile">
            <ed-button button-class="btn btn-actionbar" @click="triggerReceiptFileInput">
              UPLOAD NEW RECEIPT
            </ed-button>
          </div>
          <div v-else style="display: flex; align-items: center; gap: 10px; padding: 12px 8px; border-bottom: 1px solid rgba(0,0,0,0.08); margin-bottom: 28px;">
            <i class="fas" :class="pendingReceiptIcon.iconClass" :style="{ color: pendingReceiptIcon.color, fontSize: '18px' }"></i>
            <a :href="pendingReceiptObjectUrl || undefined" target="_blank" rel="noopener" style="color: #4a90e2; text-decoration: underline; flex: 1; font-size: 12px;">{{ pendingReceiptFile.name }}</a>
            <span style="color: #999; font-size: 12px;">{{ formatFileSize(pendingReceiptFile.size) }}</span>
            <a href="javascript:;" @click="clearPendingReceiptFile" v-tooltip="'Remove'" style="line-height: 0;">
              <i class="fas fa-times-circle" style="color: #888; font-size: 18px;"></i>
            </a>
          </div>
        </template>
      </div>
    </ed-modal>
    <ed-modal :type="fields && fields.length > 0 && fields[0] && fields[0].length > 0 ? 'extra-large' : 'medium-large'"
              title="Reconcile Transaction"
              :show="showReconcile"
              @close="showModal(showReconcile); closeReconcile()"
              @validate="saveAndReconcile"
              :primary-button="(isUserAdmin || reconcileAction !== 'view') ? 'Save' : 'false'"
              :primaryButtonDisabled="!uploadFinished"
              secondary-button="Cancel"
              validation>
      <Reconcile slot="body"
                 v-if="transaction && transaction.data"
                 :key="transaction.data.Id"
                 :transaction="transaction"
                 :fields-incomplete="{ receiptIncomplete, descriptionIncomplete }"
                 :reconcile-action="reconcileAction"
                 @reconcile-created="handleReconcileCreated"
                 @fields-updated="handleFieldsUpdated"
                 @mediaFilesPathUpdated="updateMediaFilesPath"
                 @attachmentsUpdated="updateAttachmentsFiles"
                 @additional-documents-updated="updateAdditionalDocuments"
                 @split-amounts="handleSplitAmounts"
                 @description-text="handleDescriptionText"
                 @uploadFinished="handleUploadFinished"
                 ref="reconcileComp">
      </Reconcile>
    </ed-modal>
  </div>
</template>

<script>

import Grid from '@/components/Grid'
import model from './model.js'
import tabs from '@/components/_modules/Programs/Card-transactions/tabs'
import CreateEnvelope from './CreateEnvelope.vue'
import Reconcile from './ReconcileTransaction.vue'
import CandidateSelector from './CandidateSelector.vue'
import receiptScansApi from '@/core/api/receiptScans'
import createReceiptScanPoller from '@/core/receiptScan/receiptScanPoller'
import compressReceiptImage from '@/core/receiptScan/imageCompression'
import featureFlags from '@/core/feature-flags/feature-flags'

import { EventBus } from '@/event-bus'
import events from '@/core/events/events'

const cancelTooltip = 'Please select a transaction.'

// reload page after browser back pressed
function handlePopState(event) {
  if (document.location.href.includes('transactions')) {
    window.location.reload()
  }
}
window.addEventListener('popstate', handlePopState)

export default {
  components: {
    Grid,
    CreateEnvelope,
    Reconcile,
    CandidateSelector
  },
  data() {
    return {
      model,
      sort: { TransactionDateFormatted: 'desc' },
      tabs: tabs.tabs,
      selectedProgram: 0,
      selectedSupplier: 0,
      selectedRows: [],
      selectedAll: null,
      showCreateEnvelope: false,
      showReconcile: false,
      fields: [],
      formKey: '',
      splitAmounts: [],
      noteText: '',
      mediaFilesPath: '',
      filteredAttachments: [],
      // PRODUCTDEV-5772 — supporting documents, kept separate from filteredAttachments
      // (receipts) so they never feed IsComplete / ReceiptFiles / Reconciliation Status.
      additionalDocumentsFiles: [],
      requireReceiptDescription: Boolean,
      descriptionIncomplete: false,
      receiptIncomplete: false,
      savePressed: false,
      reconcileAction: 'create',
      selectedBu: [],
      selectedCc: [],
      selectedBuAndCcPair: [],
      selectedCategory: [],
      referenceId: [],
      descriptions: [],
      splitCounter: 1,
      buAndCcPairIncomplete: [],
      buIncomplete: [],
      ccIncomplete: [],
      categoryIncomplete: [],
      itemIds: [],
      transaction: {},
      approvalUsersData: [],
      highlightedTransactionId: '',
      rowClasses: {
        'row-highlight': params => {
          return (params.data && (params.data.Id === this.highlightedTransactionId || params.data.EnvelopeId === this.highlightedTransactionId))
        }
      },
      envelope: {},
      noTooltipFields: ['TransactionStatus', 'ReconciliationStatus', 'TransactionNumber', 'EnvelopeNumber'],
      waitingApprovalStatuses: ['Open - Waiting Approval', 'Exported - Waiting Approval', 'Posted - Waiting Approval'],
      uploadFinished: true,
      receiptScanEnabled: featureFlags.receiptScanPrefill,
      // PRODUCTDEV-5788 — global all-transactions table has no single division in context,
      // so the Submit Receipt button shows iff >=1 of the user's divisions is scan-enabled
      // (tenant-wide any-enabled check). Default false so it stays hidden until the check
      // confirms; never shows when every division is "No Receipt Capture".
      anyScanEnabled: false,
      uploadingReceipt: false,
      uploadProgressPct: 0,
      showSubmitReceiptModal: false,
      pendingReceiptFile: null,
      // PRODUCTDEV-5804 — object URL for the pending file so the chip link carries a real href
      // (right-click "Open in new tab"). Lifecycle owned by the pendingReceiptFile watcher.
      pendingReceiptObjectUrl: '',
      showCandidateSelector: false,
      candidateSelectorReceiptId: null,
      candidateSelectorCandidates: [],
      candidateSelectorRowVersion: null,
      candidateSelectorAutoLinked: false,
      candidateSelectorAutoFill: null,
      candidateSelectorLoading: false,
      candidateSelectorMatching: false,
      candidateSelectorLocalImageUrl: null,
      candidateSelectorLocalImageType: null,
      pendingLegacyReceipt: null,
      activePoller: null
    }
  },

  async created() {
    this.divisionName = this.singularize(this.$root.getUser.settings.Labels.DivisionName)
    this.divisionsName = this.$root.getUser.settings.Labels.DivisionsName
    this.model.Division.label = this.divisionName
    this.programName = this.singularize(this.$root.getUser.settings.Labels.ProgramLabel)
    this.model.ProgramName.label = this.programName
    EventBus.$off(events.EVEDPAY_CARDS.OPEN_RECONCILE)
    EventBus.$on(events.EVEDPAY_CARDS.OPEN_RECONCILE, this.handleTransaction)

    EventBus.$off('receipt-scan-started')
    EventBus.$on('receipt-scan-started', (payload) => {
      this.handleReceiptScanStarted(payload.data)
    })

    EventBus.$off(events.EVEDPAY_CARDS.OPEN_ENVELOPE)
    EventBus.$on(events.EVEDPAY_CARDS.OPEN_ENVELOPE, envelope => {
      this.envelope = envelope.data
      this.showCreateEnvelope = true
    })

    // PRODUCTDEV-5788 — resolve once whether any of the user's divisions is scan-enabled;
    // drives the global Submit Receipt button's visibility. Default-false on any failure.
    // Deferred to $nextTick: on a hard refresh created() runs during app bootstrap, before
    // App.vue's `global.app = new Vue(...)` is assigned, so the httpPlatform request
    // interceptor can't attach the bearer yet and the call would fire anonymously (401).
    // By the next tick bootstrap has finished and global.app is set, so the request goes out
    // authenticated and the button resolves correctly instead of being wrongly hidden.
    if (this.receiptScanEnabled) {
      this.$nextTick(() => {
        receiptScansApi.getAnyScanEnabled()
          .then((res) => { this.anyScanEnabled = !!(res && res.anyEnabled) })
          .catch((e) => {
            console.warn('Could not fetch any-scan-enabled (Submit Receipt hidden):', e)
            this.anyScanEnabled = false
          })
      })
    }

    EventBus.$off('transaction-reconciled')
    EventBus.$on('transaction-reconciled', (transactionId) => {
      if (this.transaction && this.transaction.data) {
        const matchedTransaction = this.transaction.data.Id === transactionId.data

        if (matchedTransaction) {
          const transactionNumber = this.transaction.data.TransactionNumber
          this.$notifications.notify({
            message: `Transaction ${transactionNumber} was updated!`,
            type: 'info',
            placement: { from: 'top', align: 'center' }
          })
        }
      }
    })
  },

  computed: {
    // PRODUCTDEV-5804 — icon glyph + color for the pending receipt chip (ed-upload palette).
    pendingReceiptIcon() {
      var name = ((this.pendingReceiptFile && this.pendingReceiptFile.name) || '').toLowerCase()
      if (/\.pdf$/.test(name)) return { iconClass: 'fa-file-pdf', color: '#cb0404' }
      if (/\.(png|jpe?g|gif|webp)$/.test(name)) return { iconClass: 'fa-file-image', color: '#4598d9' }
      return { iconClass: 'fa-file', color: '#acacac' }
    },
    selectedId() {
      return this.transaction && this.transaction.data &&
        this.transaction.data.PaymentInvoiceID
    },
    isUserAdmin() {
      if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
        return true
      }
      return false
    }
  },

  activated() {
    this.selectedRows = []
    this.envelope = {}
  },

  watch: {
    // PRODUCTDEV-5804 — own the pending file's object URL lifecycle (revoke old, create new) so the
    // chip link has a stable href. Covers every assignment site to pendingReceiptFile.
    pendingReceiptFile(file) {
      if (this.pendingReceiptObjectUrl) { URL.revokeObjectURL(this.pendingReceiptObjectUrl); this.pendingReceiptObjectUrl = '' }
      if (file) this.pendingReceiptObjectUrl = URL.createObjectURL(file)
    },
    showCreateEnvelope(newValue) {
      if (newValue) {
        this.highlightedTransactionId = this.envelope.Id
      }
    },
    showCandidateSelector(newVal) {
      if (!newVal && this.candidateSelectorLocalImageUrl) {
        URL.revokeObjectURL(this.candidateSelectorLocalImageUrl)
        this.candidateSelectorLocalImageUrl = null
        this.candidateSelectorLocalImageType = null
      }
    }
  },

  methods: {
    stopSave() {
      this.savePressed = false
    },

    async createReconcile() {
      let hasIncompleteField = false

      if (!(this.selectedProgram && Object.keys(this.selectedProgram).length !== 0 && this.selectedProgram.Id)) {
        hasIncompleteField = true
      }

      if ((!this.noteText || this.noteText && this.noteText.length === 0) && this.requireReceiptDescription && this.filteredAttachments.length !== 0) {
        this.descriptionIncomplete = true
        hasIncompleteField = true
      } else {
        this.descriptionIncomplete = false
      }

      if (this.filteredAttachments.length === 0) {
        hasIncompleteField = true
      }

      if (this.transaction.data.EnvelopeId &&
        this.transaction.data.EnvelopeId !== '00000000-0000-0000-0000-000000000000' &&
        this.filteredAttachments.length === 0 &&
        this.transaction.data.EnvelopeStatus !== 'Draft'
      ) {
        this.$notifications.notify({
          type: 'info-warn',
          message: 'Receipt is required. Please upload at least one file.',
          placement: { from: 'top', align: 'center' }
        })
        return this.stopSave()
      }

      this.buAndCcPairIncomplete = new Array(this.splitCounter).fill(false)
      this.buIncomplete = new Array(this.splitCounter).fill(false)
      this.ccIncomplete = new Array(this.splitCounter).fill(false)
      this.categoryIncomplete = new Array(this.splitCounter).fill(false)

      for (var i = 0; i < this.splitCounter; i++) {
        if (this.selectedBuAndCcPair) {
          this.buAndCcPairIncomplete[i] = !this.selectedBuAndCcPair[i]
        } else if (this.selectedBu && this.selectedCc) {
          this.buIncomplete[i] = !this.selectedBu[i]
          this.ccIncomplete[i] = !this.selectedCc[i]
        } else if (this.selectedBu && !this.selectedCc) {
          this.buIncomplete[i] = !this.selectedBu[i]
        } else if (!this.selectedBu && this.selectedCc) {
          this.ccIncomplete[i] = !this.selectedCc[i]
        }

        if (this.buAndCcPairIncomplete[i] || this.buIncomplete[i] || this.ccIncomplete[i]) {
          hasIncompleteField = true
        }

        if (!(this.selectedCategory && this.selectedCategory[i])) {
          hasIncompleteField = true
        }

        // check incomplete custom fields
        if (this.fields.some(item =>
          item.some(field => field && field.Required && (!field.Values || field.Values.length === 0))
        )) {
          hasIncompleteField = true
        }
      }

      if (this.descriptionIncomplete) {
        return this.stopSave()
      }

      try {
        const formCopy = JSON.parse(JSON.stringify(this.fields))
        const updatedFields = formCopy.map(i => {
          return i.map(x => {
            if (x.Type === 'select' && x.Values && typeof x.Values === 'object') {
              x.Values = x.Values.text
            } else if (x.Type === 'text' && x.Values && Array.isArray(x.Values) && x.Values.length > 0) {
              x.Values = x.Values[0]
            }
            return x
          })
        })

        let form = {
          Fields: updatedFields,
          CategoryIds: this.selectedCategory,
          ReferenceIds: this.referenceId,
          Descriptions: this.descriptions,
          BusinessUnitIds: this.selectedBu,
          CostCenterIds: this.selectedCc,
          ProgramBusinessUnitAndCostCenterIds: this.selectedBuAndCcPair,
          Amounts: this.splitAmounts,
          TransactionReconcileSplitItemIds: this.itemIds
        }

        if (this.selectedCardId) {
          // Defensive padding — every per-row array must have splitCounter entries or
          // Legacy's SaveTransactionFields (EvedPayCardsRepository.cs:3848) throws
          // IndexOutOfRange when it iterates Amounts.Count and accesses parallel arrays.
          // Photon-driven splits can outpace the emit chain that syncs these arrays back
          // up, leaving shorter-than-expected lengths at save time — especially on a
          // fresh SUBMIT-match reconcile where the rows come from prefill and never
          // had server-side ItemIds assigned. Mirrors Card-list/index.vue:1818-1862.
          const padArray = (arr, len, fill) => {
            const out = Array.isArray(arr) ? arr.slice() : []
            while (out.length < len) {
              out.push(fill === undefined ? null : fill)
            }
            return out
          }
          const rowCount = this.splitCounter || 1
          const paddedSelectedBu = padArray(this.selectedBu, rowCount)
          const paddedSelectedCc = padArray(this.selectedCc, rowCount)
          const paddedSelectedBuAndCcPair = padArray(this.selectedBuAndCcPair, rowCount)
          const paddedSelectedCategory = padArray(this.selectedCategory, rowCount)
          const paddedReferenceId = padArray(this.referenceId, rowCount)
          const paddedDescriptions = padArray(this.descriptions, rowCount, '')
          const paddedSplitAmounts = padArray(this.splitAmounts, rowCount, 0)
          const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
          const paddedItemIds = padArray(this.itemIds, rowCount, EMPTY_GUID)
            .map(id => (id == null || id === '') ? EMPTY_GUID : id)

          form.BusinessUnitIds = paddedSelectedBu
          form.CostCenterIds = paddedSelectedCc
          form.ProgramBusinessUnitAndCostCenterIds = paddedSelectedBuAndCcPair
          form.CategoryIds = paddedSelectedCategory
          form.ReferenceIds = paddedReferenceId
          form.Descriptions = paddedDescriptions
          form.Amounts = paddedSplitAmounts
          form.TransactionReconcileSplitItemIds = paddedItemIds

          const response = await this.api.post(this.api.$.evedpaycard.reconcile, {
            preventError: true,
            ...this.newReconcile,
            CardId: this.selectedCardId,
            TransactionId: this.selectedTransactionId,
            SupplierId: this.selectedSupplier && this.selectedSupplier.Value ? this.selectedSupplier.Value.VendorId : null,
            ProgramId: this.selectedProgram && this.selectedProgram.Id ? this.selectedProgram.Id : null,
            ReceiptFiles: this.filteredAttachments,
            AdditionalDocuments: this.additionalDocumentsFiles,
            Note: this.noteText,
            SplitAmounts: paddedSplitAmounts,
            BusinessUnitIds: paddedSelectedBu,
            CostCenterIds: paddedSelectedCc,
            BusinessUnitAndCostCenterIds: paddedSelectedBuAndCcPair,
            CategoryIds: paddedSelectedCategory,
            ReferenceIds: paddedReferenceId,
            Descriptions: paddedDescriptions,
            IsComplete: !hasIncompleteField,
            Form: form,
            FormKey: this.formKey
          })
          if (response.data) {
            return true
          } else {
            console.error('Error during the request. Response data is null.')
            this.notifyReconcileError('An error occurred while saving. Please try again.')
          }
        } else {
          console.error('Selected CardId is null or undefined.')
          this.notifyReconcileError('An error occurred while saving. Please try again.')
        }
      } catch (error) {
        console.error('Error during the request:', error)
        const data = error && error.response && error.response.data
        const errorsArr = (data && data.Errors) || (Array.isArray(data) ? data : null)
        const apiMessage = errorsArr && errorsArr[0] && errorsArr[0].Message
        this.notifyReconcileError(apiMessage || 'An error occurred while saving. Please try again.')
      }
    },

    notifyReconcileError(message) {
      this.$notifications.notify({
        type: 'plain',
        icon: 'exclamation-circle',
        placement: { from: 'top', align: 'center' },
        timeout: 0,
        message,
        discard: 'Ok',
        goto: ''
      })
    },

    createEnvelope() {
      this.showCreateEnvelope = true
      window.history.pushState({}, '', '#/d/Programs/>Card/envelopes')
    },

    resetValidation() {
      this.receiptIncomplete = false
      this.descriptionIncomplete = false
      this.buAndCcPairIncomplete = new Array(this.splitCounter).fill(false)
      this.buIncomplete = new Array(this.splitCounter).fill(false)
      this.ccIncomplete = new Array(this.splitCounter).fill(false)
      this.categoryIncomplete = new Array(this.splitCounter).fill(false)
    },

    async saveAndReconcile() {
      if (!this.savePressed) {
        this.savePressed = true
        try {
          // PRODUCTDEV-5790 — submitted / non-Draft-envelope transactions are coding-locked.
          // Regular users can't save at all (view-locked). A card admin can still replace the
          // receipt + add/remove additional documents via the narrow update-receipt endpoint,
          // WITHOUT re-saving coding. Mirrors Card-list/index.vue. (Current approvers resolve to
          // 'edit', not 'view', so they take the normal createReconcile path below.)
          if (this.reconcileAction === 'view') {
            if (this.isUserAdmin) {
              await this.updateReceiptAndDocuments()
            }
            return this.stopSave()
          }
          const r = await this.createReconcile()
          if (r) {
            EventBus.$emit('transaction-reconciled', this.transaction.data.Id)
            this.savePressed = false
            this.closeReconcile({ userCancelled: false })
            this.resetModalFields()
          }
        } finally {
          this.savePressed = false
        }
      }
    },

    // PRODUCTDEV-5790 — receipt/document-only save for a card admin on a view-locked
    // (submitted-envelope) transaction. Persists via the narrow update-receipt endpoint so
    // coding is never touched. Sends the FULL desired document set: the backend keeps docs by
    // Id and soft-deletes any existing doc not in the list, so this is how an admin's add/remove
    // is applied. (additionalDocumentsFiles is populated from the modal on open, so an untouched
    // doc set round-trips unchanged.)
    async updateReceiptAndDocuments() {
      if (this.filteredAttachments.length === 0) {
        this.$notifications.notify({
          type: 'info-warn',
          message: 'Receipt is required. Please upload at least one file.',
          placement: { from: 'top', align: 'center' }
        })
        return
      }
      const mapFile = f => ({
        id: f.Id || '00000000-0000-0000-0000-000000000000',
        pathWithName: f.PathWithName,
        originalName: f.OriginalName,
        mediaFile: f.MediaFile,
        sizeBytes: f.SizeBytes,
        vendorId: f.VendorId
      })
      const payload = {
        transactionId: this.selectedTransactionId,
        receiptFiles: this.filteredAttachments.map(mapFile),
        additionalDocuments: this.additionalDocumentsFiles.map(mapFile)
      }
      try {
        await this.api.post(this.api.$.evedpaycard.updateReceipt, payload)
        EventBus.$emit('transaction-reconciled', this.transaction.data.Id)
        this.$notifications.notify({
          type: 'info',
          message: 'Receipt updated successfully',
          placement: { from: 'top', align: 'center' }
        })
        this.closeReconcile({ userCancelled: false })
        this.resetModalFields()
      } catch (e) {
        console.error('Error while updating the receipt:', e)
        this.notifyReconcileError('An error occurred while saving. Please try again.')
      }
    },

    updateMediaFilesPath(mediaFilesPath) {
      this.mediaFilesPath = mediaFilesPath
      this.receiptIncomplete = false
    },

    updateAttachmentsFiles(filteredAttachments) {
      this.filteredAttachments = filteredAttachments
    },

    // PRODUCTDEV-5772 — supporting documents from the reconcile modal. Stored apart
    // from filteredAttachments and posted as AdditionalDocuments; never part of
    // ReceiptFiles / IsComplete / Reconciliation Status.
    updateAdditionalDocuments(additionalDocuments) {
      this.additionalDocumentsFiles = additionalDocuments
    },

    handleFieldsUpdated(form) {
      this.fields = form.fields
      this.formKey = form.formKey
    },

    handleSplitAmounts(data) {
      this.splitAmounts = data
    },

    handleDescriptionText(data) {
      this.noteText = data.noteText
      this.requireReceiptDescription = data.requireReceiptDescription
    },

    handleReconcileCreated(data) {
      this.fields = data.fields ? data.fields : this.fields
      this.itemIds = data.itemIds
      this.splitCounter = data.splitCounter
      this.selectedSupplier = data.selectedSupplier
      this.selectedProgram = data.selectedProgram
      this.selectedBu = data.selectedBu
      this.selectedCc = data.selectedCc
      this.selectedBuAndCcPair = data.selectedBuAndCcPair
      this.selectedCategory = data.selectedCategory
      this.referenceId = data.referenceId
      this.descriptions = data.descriptions
    },

    handleUploadFinished(finished) {
      this.uploadFinished = finished
    },

    singularize(value) {
      return value.endsWith('s') ? value.slice(0, -1) : value
    },

    cardActionDisabled(actionName) {
      return !!this.actionDisabledReason(actionName)
    },

    actionDisabledReason(actionName) {
      if (this.selectedRows.length > 1 && actionName === 'reconcile') {
        return 'You can only reconcile one transaction at a time.'
      }

      if (this.selectedRows.length > 0) {
        const firstCardId = this.selectedRows[0].data.CardId
        const programIds = Array.from(new Set(this.selectedRows.map(row => row && row.data.ProgramId !== null ? row.data.ProgramId : null).filter(id => id !== null)))

        if (programIds.length > 1 && actionName === 'createEnvelope') {
          return `Only transactions from the same ${this.programName.toLowerCase()} that are not already part of an envelope can be selected.`
        }

        for (var row of this.selectedRows) {
          if (row.data.TransactionStatus === 'Rejected' || row.data.TransactionStatus === 'Declined') {
            return 'Only transactions that are not Rejected or Declined can be selected.'
          }

          if (row.data.TransactionStatus === 'Pending' && actionName !== 'reconcile') {
            return 'Only transactions that are not Pending or Declined can be selected.'
          }

          if (row.data.EnvelopeNumber !== null && actionName === 'createEnvelope') {
            return 'Only transactions that are not already part of an envelope can be selected.'
          }

          if (row.data.CardId !== firstCardId) {
            return 'Only transactions from the same card that are not already part of an envelope can be selected.'
          }
        }
      }

      if (this.selectedRows.length === 0) {
        return cancelTooltip
      }
    },

    async rowSelected(event) {
      if (typeof event === 'boolean') {
        this.selectedAll = event
      } else {
        this.selectedRows = event
        this.transaction = this.selectedRows[0]
        if (this.highlightedTransactionId && this.highlightedTransactionId.length > 0 && !this.showReconcile && this.selectedRows.length > 0) {
          this.highlightedTransactionId = ''
          this.redrawRows()
        }
        if (this.transaction && this.transaction.data) {
          this.selectedCardId = this.transaction.data.CardId
          this.selectedTransactionId = this.transaction.data.Id
          // PRODUCTDEV-5783 — reset before the conditional reload so isUserInApprovalList()
          // reflects THIS transaction's envelope. getApprovalUsers only runs for waiting-approval
          // envelopes, so without this a previously-opened approver envelope's data could leak
          // through and wrongly grant 'edit' on a submitted (read-only) transaction.
          this.approvalUsersData = []
          if (this.waitingApprovalStatuses.includes(this.transaction.data.EnvelopeStatus)) {
            await this.getApprovalUsers(this.transaction.data.EnvelopeId)
          }
          await this.handleReconcileAction(this.transaction)
        } else {
          this.reconcileAction = 'create'
        }
      }
    },

    createReconciliationEnvelope() {
      if (this.selectedRows.length === 0) {
        console.error('No data')
        return
      }
    },

    showModal(showReconcile) {
      if (!this.savePressed) {
        this.showReconcile = !showReconcile
        if (this.showReconcile) {
          this.highlightedTransactionId = this.transaction.data.Id
        } else {
          this.redrawRows()
        }
        this.resetModalFields()
      }
    },

    resetFields() {
      this.savePressed = false
      this.selectedProgram = ''
      this.selectedSupplier = ''
      this.noteText = ''
      this.mediaFilesPath = ''
      this.filteredAttachments = []
      this.additionalDocumentsFiles = []
      this.fields = []
      this.formKey = ''
    },

    resetModalFields() {
      if (this.showReconcile) {
        if (this.$refs.reconcileComp) {
          this.$refs.reconcileComp.$refs.formRef.reset()
          this.resetValidation()
          this.resetFields()
        }
      } else {
        this.$refs.grid.onSelectAll(false)
        this.selectedRows = []
        this.transaction = {}
        this.approvalUsersData = []
        this.$refs.reconcileComp.$refs.formRef.reset()
        this.resetValidation()
        this.resetFields()
      }
    },

    closeReconcile(opts) {
      // userCancelled defaults to true so existing X/cancel callers behave unchanged;
      // saveAndReconcile passes userCancelled:false so cleanupUnsavedReceipts doesn't
      // delete the ReceiptScan that was just linked. Everything else still runs —
      // the modal closes / state resets either way.
      var userCancelled = !opts || opts.userCancelled !== false
      if (userCancelled) {
        if (this.$refs.reconcileComp && typeof this.$refs.reconcileComp.cleanupUnsavedReceipts === 'function') {
          // Soft-delete any Platform receipt-scans uploaded in this session before tearing
          // down the modal — fire-and-forget so close stays responsive.
          this.$refs.reconcileComp.cleanupUnsavedReceipts()
        }
      } else if (this.$refs.reconcileComp && typeof this.$refs.reconcileComp.claimPendingFolderReceipts === 'function') {
        // PRODUCTDEV-5783 — reconcile saved: claim any receipt picked from the folder this
        // session (deferred FolderSaved → Matched) so it leaves the folder only on save.
        this.$refs.reconcileComp.claimPendingFolderReceipts()
      }
      this.showReconcile = false
      this.selectedRows = []
      this.transaction = {}
      this.approvalUsersData = []
      // PRODUCTDEV-5802 — the reconcile child is destroyed with the modal, so an
      // upload in flight at close time can never emit uploadFinished(true). Reset
      // here or the NEXT reconcile opens with Save permanently disabled.
      this.uploadFinished = true
      this.refresh()
      this.resetValidation()
      this.resetFields()
      // Strip receiptScanId from URL so a later reconcile opened from the
      // grid doesn't accidentally re-apply the just-matched scan's prefill.
      if (this.$route.query.receiptScanId) {
        const cleaned = { ...this.$route.query }
        delete cleaned.receiptScanId
        this.$router.replace({ query: cleaned }).catch(() => {})
      }
    },

    async handleTransaction(transaction) {
      this.transaction = transaction
      if (this.transaction && this.transaction.data) {
        this.selectedCardId = this.transaction.data.CardId
        this.selectedTransactionId = this.transaction.data.Id
        // PRODUCTDEV-5783 — reset before the conditional reload (see rowSelected) so a previously
        // opened approver envelope's data can't leak through and wrongly grant 'edit' here.
        this.approvalUsersData = []
        if (this.waitingApprovalStatuses.includes(this.transaction.data.EnvelopeStatus)) {
          await this.getApprovalUsers(this.transaction.data.EnvelopeId)
        }
        await this.handleReconcileAction(this.transaction)
      } else {
        this.reconcileAction = 'create'
      }
      this.showReconcile = true
      this.highlightedTransactionId = this.transaction.data.Id
    },

    async handleReconcileAction(transaction) {
      const readonlyStatuses = ['In Envelope', 'Exported', 'Posted']
      if (transaction.data.ReconciliationStatus !== 'Open') {
        if (transaction.data.ReconciliationStatus === 'Coded') {
          if (transaction.data.EnvelopeStatus === 'Draft') {
            this.reconcileAction = 'edit'
          } else if (!readonlyStatuses.includes(transaction.data.TransactionStatus) || this.isUserInApprovalList()) {
            this.reconcileAction = 'edit'
          } else {
            this.reconcileAction = 'view'
          }
        } else {
          this.reconcileAction = 'view'
        }
      } else if (readonlyStatuses.includes(transaction.data.TransactionStatus)) {
        if (transaction.data.EnvelopeStatus === 'Draft') {
          this.reconcileAction = 'edit'
        } else if (this.isUserInApprovalList()) {
          this.reconcileAction = 'edit'
        } else {
          this.reconcileAction = 'view'
        }
      } else {
        this.reconcileAction = 'create'
      }
    },

    async getApprovalUsers(envelopeId) {
      await this.api.get(this.api.$.evedpaycard.approvalUsersForEnvelope, {
        params: {
          envelopeId
        }
      })
        .then(response => {
          this.approvalUsersData = response.data
        })
        .catch(error => {
          console.error('There was a problem with the approval users request:', error)
        })
    },

    isUserInApprovalList() {
      const currentUserID = this.$root.getUser.$.user.UserID
      if (Array.isArray(this.approvalUsersData)) {
        return this.approvalUsersData.some(user => user.Id === currentUserID && user.IsCurrentAction)
      }
      return false
    },

    updateUrlBackToTransactionsPage() {
      window.history.pushState({}, '', '#/d/Programs/>Card/transactions')
    },

    refresh() {
      this.$refs.grid.refreshGrid()
    },

    redrawRows() {
      this.$refs.grid.gridOptions.api.redrawRows()
    },

    openReceiptUpload() {
      this.pendingReceiptFile = null
      this.showSubmitReceiptModal = true
    },

    triggerReceiptFileInput() {
      this.$refs.receiptUploadInput.click()
    },

    handleReceiptFileSelected(event) {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }
      const file = files[0]

      const ALLOWED = ['pdf', 'png', 'jpg', 'jpeg']
      const parts = file.name ? file.name.split('.') : []
      const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
      if (!ALLOWED.includes(extension)) {
        this.$notifications.notify({
          type: 'info-warn',
          message: 'Please upload a file with one of these extensions: .pdf, .png, .jpg, .jpeg',
          placement: { from: 'top', align: 'center' }
        })
        if (this.$refs.receiptUploadInput) {
          this.$refs.receiptUploadInput.value = ''
        }
        return
      }

      const MAX_FILE_SIZE = 10 * 1024 * 1024
      if (file.size >= MAX_FILE_SIZE) {
        this.$notifications.notify({
          type: 'info-warn',
          message: 'File is too large. Please upload a receipt smaller than 10 MB.',
          placement: { from: 'top', align: 'center' }
        })
        if (this.$refs.receiptUploadInput) {
          this.$refs.receiptUploadInput.value = ''
        }
        return
      }

      this.pendingReceiptFile = file
    },

    clearPendingReceiptFile() {
      this.pendingReceiptFile = null
      if (this.$refs.receiptUploadInput) {
        this.$refs.receiptUploadInput.value = ''
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + 'b'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'Kb'
      return (bytes / (1024 * 1024)).toFixed(1) + 'Mb'
    },

    closeSubmitReceiptModal() {
      if (this.uploadingReceipt) return
      this.showSubmitReceiptModal = false
      this.pendingReceiptFile = null
      if (this.$refs.receiptUploadInput) {
        this.$refs.receiptUploadInput.value = ''
      }
    },

    async confirmSubmitReceipt() {
      if (!this.pendingReceiptFile) {
        return
      }

      const file = await compressReceiptImage(this.pendingReceiptFile)
      this.uploadingReceipt = true
      this.uploadProgressPct = 0
      this.pendingLegacyReceipt = null

      const barDuration = 4000
      const start = performance.now()
      const barComplete = new Promise(resolve => {
        const tick = (now) => {
          const pct = Math.min(100, Math.round(((now - start) / barDuration) * 100))
          this.uploadProgressPct = pct
          if (pct < 100) {
            this._uploadProgressRaf = requestAnimationFrame(tick)
          } else {
            this._uploadProgressRaf = null
            resolve()
          }
        }
        this._uploadProgressRaf = requestAnimationFrame(tick)
      })

      const legacyUrl = this.api && this.api.env ? this.api.env + this.api.$.evedpaycard.receipt : null
      const legacyToken = this.$user && this.$user.sessionSettings ? this.$user.sessionSettings.Token : ''
      const legacyPromise = legacyUrl
        ? (function (self) {
            var lf = new FormData()
            lf.append('attachment_upload', file)
            return self.api.post(legacyUrl, lf, {
              headers: { 'Authorization': 'Bearer ' + legacyToken, 'Content-Type': 'multipart/form-data' }
            }).then(function (resp) {
              var body = resp && resp.data
              if (typeof body === 'string') {
                try { body = JSON.parse(body) } catch (e) { body = null }
              }
              if (!body || !body.success || !body.path) return null
              var m = body.path.match(/(ClientData\/.*)/)
              var pathWithName = m && m[1] ? m[1] : body.path
              var mediaFile = body.file || pathWithName.split('/').pop()
              return {
                path: body.path,
                pathWithName: pathWithName,
                mediaFile: mediaFile,
                file: mediaFile,
                name: file.name,
                type: file.type || '',
                size: file.size || 0
              }
            }).catch(function (err) {
              console.warn('Legacy receipt upload failed at Transactions SUBMIT:', err)
              return null
            })
          })(this)
        : Promise.resolve(null)

      const formData = new FormData()
      formData.append('file', file)

      const idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0
          var v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })

      // Global Transactions tab: no Division to narrow by. BE treats null
      // submitExtra as "no narrowing" and ranks against all programs the user
      // can access.
      const submitExtra = null

      this.$notifications.notify({
        type: 'info',
        message: 'Scanning receipt...',
        placement: { from: 'top', align: 'center' }
      })
      const uploadPromise = receiptScansApi.submitReceipt(formData, idempotencyKey, submitExtra)

      try {
        await barComplete

        this.uploadingReceipt = false

        if (this.candidateSelectorLocalImageUrl) {
          URL.revokeObjectURL(this.candidateSelectorLocalImageUrl)
        }
        this.candidateSelectorLocalImageUrl = URL.createObjectURL(file)
        this.candidateSelectorLocalImageType = file.type || null

        this.closeSubmitReceiptModal()

        this.candidateSelectorReceiptId = null
        this.candidateSelectorCandidates = []
        this.candidateSelectorRowVersion = null
        this.candidateSelectorAutoLinked = false
        this.candidateSelectorAutoFill = null
        this.candidateSelectorLoading = true
        this.showCandidateSelector = true

        const [data, legacyMeta] = await Promise.all([uploadPromise, legacyPromise])
        this.pendingLegacyReceipt = legacyMeta || null

        if (!this.showCandidateSelector) {
          if (data && data.receiptScanId) {
            receiptScansApi.deleteReceipt(data.receiptScanId).catch(function (err) {
              console.warn('Receipt dispose on mid-upload dismiss failed (ignored):', err)
            })
          }
          return
        }

        if (data && data.receiptScanId) {
          EventBus.$emit('receipt-scan-started', {
            receiptScanId: data.receiptScanId,
            source: 'transactionsTab'
          })
          if (legacyUrl && !legacyMeta) {
            this.$notifications.notify({
              type: 'info-warn',
              message: "Receipt scanned, but it couldn't be attached to the transaction record. Match will still work, but the receipt won't appear on reopen. Cancel and retry if you need the attachment preserved.",
              placement: { from: 'top', align: 'center' }
            })
          }
        }
      } catch (error) {
        this.uploadingReceipt = false
        this.closeSubmitReceiptModal()

        // PRODUCTDEV-5851 — the image couldn't be scanned (e.g. dimensions out of range), but the
        // backend still persisted the receipt (ReadyForMatch, zero candidates) and returns 422 with
        // its receiptScanId. Silently drive the SAME post-scan flow (no error toast) so the user can
        // match manually or save it to their folder. Isolated to this one status — every other error
        // keeps its handling below.
        if (error.response && error.response.status === 422 && error.response.data && error.response.data.receiptScanId) {
          EventBus.$emit('receipt-scan-started', {
            receiptScanId: error.response.data.receiptScanId,
            source: 'transactionsTab'
          })
          return
        }

        if (this.showCandidateSelector && this.candidateSelectorLoading && !this.candidateSelectorReceiptId) {
          this.showCandidateSelector = false
          this.candidateSelectorLoading = false
        }

        if (error.isPlatformSentinelError) {
          this.$notifications.notify({
            type: 'info-warn',
            message: 'Receipt capture is temporarily unavailable. Please try again later.',
            placement: { from: 'top', align: 'center' }
          })
          return
        }
        this.$notifications.notify({
          type: 'info-warn',
          message: 'Failed to upload receipt. Please try again.',
          placement: { from: 'top', align: 'center' }
        })
        console.error('Error uploading receipt:', error)
      } finally {
        if (this._uploadProgressRaf) {
          cancelAnimationFrame(this._uploadProgressRaf)
          this._uploadProgressRaf = null
        }
        this.uploadingReceipt = false
        this.uploadProgressPct = 0
      }
    },

    handleReceiptScanStarted(data) {
      const self = this
      const receiptScanId = data.receiptScanId

      if (this.activePoller) { this.activePoller.cancel() }

      this.candidateSelectorReceiptId = receiptScanId
      this.candidateSelectorCandidates = []
      this.candidateSelectorRowVersion = null
      this.candidateSelectorAutoLinked = false
      this.candidateSelectorAutoFill = null
      this.candidateSelectorLoading = true
      this.showCandidateSelector = true

      const poller = createReceiptScanPoller({
        receiptScanId,
        contextHint: { source: data.source },
        onStateChange() {},
        onDone() {
          self.activePoller = null
          receiptScansApi.getStatus(receiptScanId)
            .then(function (statusData) {
              var candidates = (statusData && statusData.matchCandidates) || []

              if (candidates.length === 1 && statusData.autoLinked) {
                self.showCandidateSelector = false
                self.candidateSelectorLoading = false
                self.candidateSelectorReceiptId = null
                self.openMatchedReconcile(candidates[0].legacyTransactionId, receiptScanId)
                return
              }

              self.candidateSelectorReceiptId = receiptScanId
              self.candidateSelectorCandidates = candidates
              self.candidateSelectorRowVersion = statusData.rowVersion
              self.candidateSelectorAutoLinked = statusData.autoLinked || false
              self.candidateSelectorAutoFill = statusData.autoFill || null
              self.candidateSelectorLoading = false
              self.showCandidateSelector = true
            })
            .catch(function (err) {
              console.error('Error fetching receipt status:', err)
              self.showCandidateSelector = false
              self.candidateSelectorLoading = false
              self.$notifications.notify({
                type: 'danger',
                message: 'Failed to retrieve receipt details. Please try again.',
                placement: { from: 'top', align: 'center' }
              })
            })
        },
        onError(error) {
          self.activePoller = null
          self.showCandidateSelector = false
          self.candidateSelectorLoading = false
          if (error.type === 'unreachable' || error.type === 'timeout') {
            self.$notifications.notify({
              type: 'info',
              message: 'Scan is taking longer than expected. We\'ll keep working on it — check the Receipt Folder later.',
              placement: { from: 'top', align: 'center' }
            })
          } else if (error.type === 'scan_failed') {
            self.$notifications.notify({
              type: 'danger',
              message: 'Scan failed. Please try uploading the receipt again.',
              placement: { from: 'top', align: 'center' }
            })
          } else {
            self.$notifications.notify({
              type: 'danger',
              message: 'Error scanning receipt. Please try again.',
              placement: { from: 'top', align: 'center' }
            })
          }
        }
      })

      this.activePoller = poller
      poller.start()
    },

    handleMatchSelected(data) {
      this.candidateSelectorReceiptId = null
      this.showCandidateSelector = false
      this.openMatchedReconcile(data.legacyTransactionId, data.receiptScanId)
    },

    async openMatchedReconcile(legacyTransactionId, receiptScanId) {
      this.stashLegacyReceiptForReconcile(receiptScanId)

      try {
        await this.$router.replace({
          query: { ...this.$route.query, receiptScanId: receiptScanId }
        })
      } catch (e) {
        // NavigationDuplicated — ignore
      }

      const prefillP = receiptScansApi.getPrefill(receiptScanId)
        .then(function (data) {
          if (data) {
            try {
              global.sessionStorage.setItem(
                'receiptScanPrefill_' + receiptScanId,
                JSON.stringify(data)
              )
            } catch (e) {
              // sessionStorage disabled — Reconcile falls back to API call
            }
          }
        })
        .catch(function () {
          // Prefill fetch failed — Reconcile retries via API path
        })

      const txP = this.api.forms.post(this.api.$.evedpaycard.transactions, {
        sort: 'TransactionStatus',
        dir: 'ASC',
        start: 0,
        limit: 1,
        isLiveGrid: true,
        filter: JSON.stringify({ Id: legacyTransactionId, searchTerm: '' })
      })

      try {
        const [, resp] = await Promise.all([prefillP, txP])
        const rows = resp && resp.data && resp.data.Data
        if (rows && rows.length > 0) {
          const tx = rows[0]
          await this.handleTransaction({
            data: {
              ...tx,
              merchant: tx.Merchant,
              amount: tx.Amount,
              date: tx.TransactionDate,
              status: tx.TransactionStatus,
              reconcileStatus: tx.ReconciliationStatus,
              id: tx.Id
            }
          })
        } else {
          this.$notifications.notify({
            type: 'warning',
            message: 'Could not open the matched transaction. Click it in the list to view.',
            placement: { from: 'top', align: 'center' }
          })
        }
      } catch (err) {
        console.error('Failed to load matched transaction for reconcile:', err)
        this.$notifications.notify({
          type: 'warning',
          message: 'Could not open the matched transaction. Click it in the list to view.',
          placement: { from: 'top', align: 'center' }
        })
      }
    },

    stashLegacyReceiptForReconcile(receiptScanId) {
      if (!receiptScanId || !this.pendingLegacyReceipt) return
      try {
        var key = 'receiptScanLegacyPath_' + receiptScanId
        global.sessionStorage.setItem(key, JSON.stringify(this.pendingLegacyReceipt))
      } catch (err) {
        console.warn('Could not stash legacy receipt metadata:', err)
      }
    },

    handleSaveToFolder() {
      this.showCandidateSelector = false
      this.candidateSelectorReceiptId = null
    },

    handleMatchAttempt() {
      this.candidateSelectorMatching = true
    },

    handleMatchError() {
      const idToDispose = this.candidateSelectorReceiptId
      this.candidateSelectorReceiptId = null
      this.candidateSelectorMatching = false
      if (idToDispose) {
        receiptScansApi.deleteReceipt(idToDispose).catch(function (err) {
          console.warn('Match-error scan dispose failed (ignored):', err)
        })
      }
    },

    handleCandidateSelectorClose() {
      const self = this
      if (self.activePoller) { self.activePoller.cancel(); self.activePoller = null }
      if (self.candidateSelectorMatching) {
        self.candidateSelectorMatching = false
        self.showCandidateSelector = false
        self.candidateSelectorLoading = false
        return
      }
      const idToDispose = self.candidateSelectorReceiptId
      self.showCandidateSelector = false
      self.candidateSelectorLoading = false
      self.candidateSelectorReceiptId = null
      if (idToDispose) {
        receiptScansApi.deleteReceipt(idToDispose).catch(function (err) {
          console.warn('Receipt dispose on cancel failed (ignored):', err)
        })
      }
    }
  },
  beforeDestroy() {
    if (this.activePoller) { this.activePoller.cancel(); this.activePoller = null }
    if (this.pendingReceiptObjectUrl) URL.revokeObjectURL(this.pendingReceiptObjectUrl)
  },
  deactivated() {
    if (this.activePoller) { this.activePoller.cancel(); this.activePoller = null }
  }
}

</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/_variables.scss";

  .grid >>> {
    .ag-row.row-highlight {
      @include theme {
        background-color: #DBE9F9;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/index.vue